/* Fauna Jam – product image lightbox
 * Replaces Horizon's full-page zoom dialog on product pages.
 * Desktop: one image at a time, arrows / keys / thumbnails, magnifier lens on hover (wheel = zoom level).
 * Touch: swipe between images, pinch or double-tap to zoom, drag to pan, back button closes.
 */
(() => {
  if (window.__fjLightbox) return;
  window.__fjLightbox = true;

  const T = Object.assign(
    {
      dialog: 'Product images',
      close: 'Close',
      prev: 'Previous image',
      next: 'Next image',
      thumb: 'Show image',
      hintFine: 'Hover to zoom · scroll to change magnification',
      hintTouch: 'Double-tap or pinch to zoom',
    },
    window.FJ_LB_I18N || {}
  );

  const CONTAINER = '.product-media-container--image';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const LENS = 220;
  const ZMIN = 1.5;
  const ZMAX = 5;
  const PINCH_MAX = 4;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  function sized(img, width) {
    const src = img.currentSrc || img.src || '';
    try {
      const u = new URL(src, location.href);
      u.searchParams.set('width', String(width));
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  function collect(container) {
    const grid = container.closest('.media-gallery__grid');
    const gallery = container.closest('media-gallery');
    const pool = grid ? Array.from(grid.children) : Array.from(gallery.querySelectorAll('slideshow-slide'));
    const items = pool.filter((el) => el.matches(CONTAINER) && el.querySelector('img'));
    const list = items.map((el) => {
      const img = el.querySelector('img');
      return {
        full: sized(img, 2400),
        mid: sized(img, 1600),
        thumb: sized(img, 200),
        alt: img.getAttribute('alt') || '',
      };
    });
    return { list, index: Math.max(0, items.indexOf(container)) };
  }

  /* ---------- DOM ---------- */
  const icon = {
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>',
  };

  let root, track, thumbs, count, hint, lens, btnPrev, btnNext, btnClose;
  let items = [];
  let index = 0;
  let isOpen = false;
  let lastFocus = null;
  let pushed = false;
  let lensZoom = 2.5;

  function build() {
    root = document.createElement('div');
    root.id = 'fj-lb';
    root.className = 'fj-lb';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', T.dialog);
    root.hidden = true;
    root.innerHTML = `
      <div class="fj-lb__bar">
        <span class="fj-lb__count" aria-live="polite"></span>
        <span class="fj-lb__hint"></span>
        <button type="button" class="fj-lb__btn fj-lb__close" aria-label="${T.close}">${icon.close}</button>
      </div>
      <div class="fj-lb__stage">
        <button type="button" class="fj-lb__btn fj-lb__nav fj-lb__nav--prev" aria-label="${T.prev}">${icon.prev}</button>
        <div class="fj-lb__viewport"><div class="fj-lb__track"></div></div>
        <button type="button" class="fj-lb__btn fj-lb__nav fj-lb__nav--next" aria-label="${T.next}">${icon.next}</button>
        <div class="fj-lb__lens" aria-hidden="true"></div>
      </div>
      <div class="fj-lb__thumbs"></div>`;
    document.body.appendChild(root);

    track = root.querySelector('.fj-lb__track');
    thumbs = root.querySelector('.fj-lb__thumbs');
    count = root.querySelector('.fj-lb__count');
    hint = root.querySelector('.fj-lb__hint');
    lens = root.querySelector('.fj-lb__lens');
    btnPrev = root.querySelector('.fj-lb__nav--prev');
    btnNext = root.querySelector('.fj-lb__nav--next');
    btnClose = root.querySelector('.fj-lb__close');

    btnClose.addEventListener('click', () => close());
    btnPrev.addEventListener('click', () => go(index - 1));
    btnNext.addEventListener('click', () => go(index + 1));
    root.addEventListener('keydown', onKey);

    // Click on empty space around the image closes (desktop + touch single tap outside image)
    root.querySelector('.fj-lb__stage').addEventListener('click', (e) => {
      if (e.target.closest('img, .fj-lb__btn')) return;
      if (dragMoved) return;
      close();
    });

    setupLens();
    setupTouch();
    window.addEventListener('resize', () => isOpen && layout(false));
  }

  function render() {
    track.innerHTML = '';
    thumbs.innerHTML = '';
    items.forEach((it, i) => {
      const slide = document.createElement('div');
      slide.className = 'fj-lb__slide';
      const frame = document.createElement('div');
      frame.className = 'fj-lb__frame';
      const img = document.createElement('img');
      img.alt = it.alt;
      img.decoding = 'async';
      img.draggable = false;
      img.dataset.i = String(i);
      frame.appendChild(img);
      slide.appendChild(frame);
      track.appendChild(slide);

      if (items.length > 1) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'fj-lb__thumb';
        b.setAttribute('aria-label', `${T.thumb} ${i + 1}`);
        b.innerHTML = `<img src="${it.thumb}" alt="" loading="lazy">`;
        b.addEventListener('click', () => go(i));
        thumbs.appendChild(b);
      }
    });
    root.classList.toggle('fj-lb--single', items.length < 2);
    hint.textContent = finePointer.matches ? T.hintFine : T.hintTouch;
  }

  function load(i) {
    const slide = track.children[i];
    if (!slide) return;
    const img = slide.querySelector('img');
    if (!img.getAttribute('src')) img.src = finePointer.matches ? items[i].full : items[i].mid;
  }

  function layout(animate) {
    track.style.transition = animate && !reducedMotion.matches ? '' : 'none';
    track.style.transform = `translate3d(${-index * 100}%,0,0)`;
    if (!animate) void track.offsetWidth;
  }

  function go(i, animate = true) {
    if (!items.length) return;
    const next = clamp(i, 0, items.length - 1);
    if (next !== index) resetZoom(index);
    index = next;
    [index - 1, index, index + 1].forEach(load);
    layout(animate);
    count.textContent = items.length > 1 ? `${index + 1} / ${items.length}` : '';
    btnPrev.disabled = index === 0;
    btnNext.disabled = index === items.length - 1;
    Array.from(thumbs.children).forEach((b, n) => b.setAttribute('aria-current', n === index ? 'true' : 'false'));
    const active = thumbs.children[index];
    if (active) active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    hideLens();
  }

  function open(list, start) {
    if (!root) build();
    items = list;
    index = start;
    render();
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('fj-lb-open');
    isOpen = true;
    go(start, false);
    requestAnimationFrame(() => root.classList.add('fj-lb--in'));
    btnClose.focus({ preventScroll: true });
    try {
      history.pushState(Object.assign({}, history.state, { fjLb: true }), '');
      pushed = true;
    } catch (e) {
      pushed = false;
    }
  }

  function close(fromHistory = false) {
    if (!isOpen) return;
    isOpen = false;
    resetZoom(index);
    hideLens();
    root.classList.remove('fj-lb--in');
    root.hidden = true;
    document.documentElement.classList.remove('fj-lb-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    if (pushed && !fromHistory) {
      pushed = false;
      history.back();
    }
    pushed = false;
  }

  window.addEventListener('popstate', () => {
    if (isOpen) close(true);
  });

  function onKey(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(index + 1);
    } else if (e.key === 'Tab') {
      const f = Array.from(root.querySelectorAll('button:not([disabled])')).filter((b) => b.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---------- Magnifier lens (mouse / trackpad) ---------- */
  function currentImg() {
    const s = track && track.children[index];
    return s ? s.querySelector('img') : null;
  }

  function hideLens() {
    if (lens) lens.classList.remove('is-on');
  }

  function moveLens(e) {
    const img = currentImg();
    if (!img || !img.complete || !img.naturalWidth) return hideLens();
    const r = img.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x < 0 || y < 0 || x > r.width || y > r.height) return hideLens();
    lens.style.backgroundImage = `url("${items[index].full}")`;
    lens.style.backgroundSize = `${r.width * lensZoom}px ${r.height * lensZoom}px`;
    lens.style.backgroundPosition = `${-(x * lensZoom - LENS / 2)}px ${-(y * lensZoom - LENS / 2)}px`;
    lens.style.transform = `translate3d(${e.clientX - LENS / 2}px, ${e.clientY - LENS / 2}px, 0)`;
    lens.classList.add('is-on');
  }

  function setupLens() {
    root.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || !finePointer.matches) return;
      if (!e.target.closest('.fj-lb__frame img')) return hideLens();
      moveLens(e);
    });
    root.addEventListener('pointerleave', hideLens);
    root.addEventListener(
      'wheel',
      (e) => {
        if (!finePointer.matches || !lens.classList.contains('is-on')) return;
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        e.preventDefault();
        lensZoom = clamp(lensZoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12), ZMIN, ZMAX);
        moveLens(e);
      },
      { passive: false }
    );
  }

  /* ---------- Touch: swipe, pinch, double-tap, pan ---------- */
  const zoomState = new Map(); // index -> {s, tx, ty}
  let dragMoved = false;

  function zs(i) {
    if (!zoomState.has(i)) zoomState.set(i, { s: 1, tx: 0, ty: 0 });
    return zoomState.get(i);
  }

  function applyZoom(i, animate) {
    const slide = track.children[i];
    if (!slide) return;
    const img = slide.querySelector('img');
    const z = zs(i);
    img.style.transition = animate && !reducedMotion.matches ? 'transform .25s ease' : 'none';
    img.style.transform = z.s === 1 ? '' : `translate3d(${z.tx}px, ${z.ty}px, 0) scale(${z.s})`;
    root.classList.toggle('fj-lb--zoomed', z.s > 1);
    if (z.s > 1.5 && img.src !== items[i].full) img.src = items[i].full;
  }

  function resetZoom(i) {
    if (!zoomState.has(i)) return;
    zoomState.set(i, { s: 1, tx: 0, ty: 0 });
    applyZoom(i, false);
  }

  // Keep the scaled image covering the frame (or centred when smaller than it)
  function clampPan(i) {
    const slide = track.children[i];
    const frame = slide.querySelector('.fj-lb__frame');
    const img = slide.querySelector('img');
    const z = zs(i);
    const fw = frame.clientWidth;
    const fh = frame.clientHeight;
    const w = img.offsetWidth * z.s;
    const h = img.offsetHeight * z.s;
    const l0 = img.offsetLeft;
    const t0 = img.offsetTop;
    z.tx = w <= fw ? (fw - w) / 2 - l0 : clamp(z.tx, fw - w - l0, -l0);
    z.ty = h <= fh ? (fh - h) / 2 - t0 : clamp(z.ty, fh - h - t0, -t0);
  }

  // Zoom to scale ns keeping the screen point (cx, cy) fixed
  function zoomAt(i, ns, cx, cy, base) {
    const slide = track.children[i];
    const img = slide.querySelector('img');
    const frame = slide.querySelector('.fj-lb__frame');
    const fr = frame.getBoundingClientRect();
    const z = zs(i);
    const b = base || z;
    const px = cx - fr.left - img.offsetLeft;
    const py = cy - fr.top - img.offsetTop;
    const ccx = (px - b.tx) / b.s;
    const ccy = (py - b.ty) / b.s;
    z.s = ns;
    z.tx = px - ccx * ns;
    z.ty = py - ccy * ns;
    clampPan(i);
  }

  function setupTouch() {
    const viewport = root.querySelector('.fj-lb__viewport');
    const pts = new Map();
    let mode = null; // 'swipe' | 'pan' | 'pinch'
    let start = null;
    let lastTap = { t: 0, x: 0, y: 0 };

    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

    viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const z = zs(index);
      if (pts.size === 1) {
        dragMoved = false;
        mode = z.s > 1 ? 'pan' : 'swipe';
        start = { x: e.clientX, y: e.clientY, tx: z.tx, ty: z.ty, t: performance.now() };
        track.style.transition = 'none';
      } else if (pts.size === 2) {
        const [a, b] = Array.from(pts.values());
        mode = 'pinch';
        layout(false);
        start = { d: dist(a, b), s: z.s, tx: z.tx, ty: z.ty };
      }
    });

    viewport.addEventListener('pointermove', (e) => {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const z = zs(index);
      if (mode === 'pinch' && pts.size >= 2) {
        const [a, b] = Array.from(pts.values());
        const m = mid(a, b);
        const ns = clamp((start.s * dist(a, b)) / start.d, 1, PINCH_MAX);
        zoomAt(index, ns, m.x, m.y, { s: z.s, tx: z.tx, ty: z.ty });
        applyZoom(index, false);
        dragMoved = true;
      } else if (mode === 'pan') {
        z.tx = start.tx + (e.clientX - start.x);
        z.ty = start.ty + (e.clientY - start.y);
        clampPan(index);
        applyZoom(index, false);
        if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 6) dragMoved = true;
      } else if (mode === 'swipe') {
        const dx = e.clientX - start.x;
        if (Math.abs(dx) > 6) dragMoved = true;
        const edge = (index === 0 && dx > 0) || (index === items.length - 1 && dx < 0);
        const off = edge ? dx / 3 : dx;
        track.style.transform = `translate3d(calc(${-index * 100}% + ${off}px),0,0)`;
      }
    });

    const end = (e) => {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      const z = zs(index);
      if (mode === 'swipe' && pts.size === 0) {
        const dx = e.clientX - start.x;
        const dt = performance.now() - start.t;
        const w = viewport.clientWidth;
        if (dx < -w * 0.18 || (dx < -40 && dt < 250)) go(index + 1);
        else if (dx > w * 0.18 || (dx > 40 && dt < 250)) go(index - 1);
        else layout(true);
      }
      if (mode === 'pinch' && pts.size < 2) {
        if (z.s < 1.05) resetZoom(index);
        else applyZoom(index, false);
        mode = pts.size === 1 ? 'pan' : null;
        if (mode === 'pan') {
          const p = Array.from(pts.values())[0];
          start = { x: p.x, y: p.y, tx: z.tx, ty: z.ty };
        }
        return;
      }
      if (pts.size === 0) {
        // double-tap
        if (!dragMoved && e.target.closest('img')) {
          const now = performance.now();
          if (now - lastTap.t < 300 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30) {
            if (z.s > 1) {
              zoomState.set(index, { s: 1, tx: 0, ty: 0 });
            } else {
              zoomAt(index, 2.5, e.clientX, e.clientY);
            }
            applyZoom(index, true);
            lastTap = { t: 0, x: 0, y: 0 };
          } else {
            lastTap = { t: now, x: e.clientX, y: e.clientY };
          }
        }
        mode = null;
        setTimeout(() => (dragMoved = false), 0);
      }
    };
    viewport.addEventListener('pointerup', end);
    viewport.addEventListener('pointercancel', end);
  }

  /* ---------- Intercept clicks on product media ---------- */
  window.addEventListener(
    'click',
    (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('#fj-lb')) return;
      const c = t.closest(CONTAINER);
      if (!c || !c.closest('media-gallery') || c.closest('.dialog-zoomed-gallery')) return;
      if (t.closest('a, deferred-media, product-model, .slideshow-control')) return;
      const { list, index: i } = collect(c);
      if (!list.length) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      open(list, i);
    },
    true
  );
})();
